import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useCurrentUser } from "@/features/auth/hooks/useSession";
import { type ReactElement, useEffect } from "react";
// OrganizationVerificationService removed - RootRoute simplified
import React from "react";
import { systemBarsService } from "@/utils/systemBars";
import ProfileGuard from "@/components/guards/ProfileGuard";
import AuthGuard from "@/components/guards/AuthGuard";
import OrganizerGuard from "@/components/guards/OrganizerGuard";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useViewportMode } from "@/hooks/useViewportMode";
import { getCurrentRole } from "@/stores/authStore";
import {
  resolveDesktopRoleForPath,
  resolveDesktopRouteRedirect,
} from "./desktopRoutePolicy";

// Desktop-only shell: lazy so it is not part of the mobile/Capacitor bundle.
const DesktopAppShell = React.lazy(
  () => import("@/components/layout/DesktopAppShell.desktop"),
);

// Import lazy-loaded pages with optimized loading states
import {
  LazyLoginScreen,
  LazyRegisterPhoneScreen,
  LazyVerifyOTPScreen,
  LazyVerifyOTPMethodScreen,
  LazyForgotPasswordScreen,
  LazyResetPasswordScreen,
  LazyProfileSetupScreen,
  LazyRedirectRoleScreen,
  LazyPlayerHomeScreen,
  LazyOrganizerHomeScreen,
  LazyAppHomeScreen,
  LazyMyScheduleScreen,
  LazyPlayerStatisticsScreen,
  LazyPlayerAchievementsScreen,
  LazyOrganizationStatisticsScreen,
  LazyNotificationScreen,
  LazySettingScreen,
  LazyAccountScreen,
  LazyUpdateProfileScreen,
  LazyChangePasswordScreen,
  LazySubscriptionScreen,
  LazyOrganizerRegistrationScreen,
  LazyMatchHistoryScreen,
  LazyRegisterTournamentScreen,
  LazyCreateTournamentScreen,
  LazyTournamentDetailScreen,
  LazyRegistrationProgressScreen,
  LazyRegistrationListScreen,
  LazyTournamentHistoryScreen,
  LazyMyTournamentScreen,
  LazyPlayerProfileScreen,
  LazyOrganizerProfileScreen,
  LazyMainTabs,
  LazyOrganizerTabs,
  LazyCompetitionDetailScreen,
  LazyCompetitionListScreen,
  LazyCompetitionScheduleScreen,
  LazyCompetitionResultsScreen,
  LazyEditBracketScreen,
  LazyDrawPositionScreen,
  LazyReportScreen,
  LazyCategoryManagementScreen,
  LazyAddPrizeScreen,
  LazyLeaderBoardScreen,
  LazyRankingInformationScreen,
  LazyPlayerRankingInformationScreen,
  LazyRankingSettlementDetail,
} from "./LazyRoutes";

// Enhanced Private Route component with profile completion check
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <AuthGuard requireAuth={true}>
      <ProfileGuard>{children}</ProfileGuard>
    </AuthGuard>
  );
};

// Legacy private route component removed - using AuthGuard instead

const ProfileSetupRoute = ({ children }: { children: ReactElement }) => {
  const { isLoading, isAuthenticated } = useCurrentUser();

  if (isLoading) return <LoadingSpinner text="Đang tải..." />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow access to ProfileSetupScreen for authenticated users
  // Don't redirect based on roles - let ProfileSetupScreen handle its own logic
  return children;
};

const AUTH_OR_ENTRY_PATHS = new Set([
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/forgot-password-confirm",
  "/verify-otp-method",
  "/verify-otp",
  "/redirect-role",
]);

// Enhanced Route Guard to prevent URL manipulation during profile setup
const RouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { isDesktop } = useViewportMode();
  const { pathname } = useLocation();

  if (!isDesktop) {
    return <>{children}</>;
  }

  const activeRole = resolveDesktopRoleForPath(pathname, getCurrentRole());

  const redirectTarget = resolveDesktopRouteRedirect(pathname, activeRole);
  if (redirectTarget && redirectTarget !== pathname) {
    return <Navigate to={redirectTarget} replace />;
  }

  const isAuthOrEntry = AUTH_OR_ENTRY_PATHS.has(pathname);
  const isTabRoute =
    pathname.startsWith("/tabs") || pathname.startsWith("/organizer-tabs");
  const isFullBleedBracket = pathname.endsWith("/manage-results");

  if (!isAuthOrEntry && !isTabRoute) {
    return (
      <React.Suspense fallback={null}>
        <DesktopAppShell role={activeRole} fullBleed={isFullBleedBracket}>
          {children}
        </DesktopAppShell>
      </React.Suspense>
    );
  }

  return <>{children}</>;
};

const PlayerTabsIndexRedirect = () => {
  const { isDesktop } = useViewportMode();
  return <Navigate to={isDesktop ? "tournaments" : "newsfeed"} replace />;
};

const OrganizerTabsIndexRedirect = () => {
  const { isDesktop } = useViewportMode();
  return <Navigate to={isDesktop ? "my-tournaments" : "newsfeed"} replace />;
};

// StatusBar Manager Component - Uses automatic route-based management
// ALSO: Detects and breaks redirect loops
const redirectHistory: { path: string; time: number }[] = [];
const REDIRECT_LOOP_THRESHOLD = 4; // Max redirects allowed
const REDIRECT_LOOP_WINDOW_MS = 2000; // Within 2 seconds

const StatusBarManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    systemBarsService
      .applyRouteConfig(pathname)
      .catch((error) =>
        console.error("❌ Route-based status bar sync failed:", error),
      );
  }, [pathname]);

  useEffect(() => {
    const now = Date.now();

    // Add current path to history
    redirectHistory.push({ path: pathname, time: now });

    // Clean up old entries
    while (
      redirectHistory.length > 0 &&
      redirectHistory[0].time < now - REDIRECT_LOOP_WINDOW_MS
    ) {
      redirectHistory.shift();
    }

    // Detect redirect loop
    if (redirectHistory.length >= REDIRECT_LOOP_THRESHOLD) {
      const paths = redirectHistory.map((r) => r.path);
      const uniquePaths = new Set(paths);

      if (
        uniquePaths.size <= 2 &&
        paths.includes("/login") &&
        paths.includes("/redirect-role")
      ) {
        console.error("🚨 REDIRECT LOOP DETECTED! History:", paths);
        redirectHistory.length = 0;
      }
    }
  }, [pathname]);

  return null;
};

// Root route component with smart navigation
const RootRoute = () => {
  const { isAuthenticated, isLoading: sessionLoading } = useCurrentUser();

  // Show loading while initializing
  if (sessionLoading) {
    return null;
  }

  // SIMPLIFIED: If no valid session, go to login. If valid session, go to redirect-role.
  // Let RedirectRoleScreen handle the role-based navigation
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Has valid session - let redirect-role screen handle role selection
  return <Navigate to="/redirect-role" replace />;
};

const AppRouter = () => {
  return (
    <>
      <StatusBarManager />
      <RouteGuard>
        <Routes>
          <Route path="/" element={<RootRoute />} />

          {/* Auth routes - Login page handles its own redirect after successful login */}
          <Route path="/login" element={<LazyLoginScreen />} />
          <Route
            path="/register"
            element={
              <AuthGuard requireNoAuth={true}>
                <LazyRegisterPhoneScreen />
              </AuthGuard>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <AuthGuard requireNoAuth={true}>
                <LazyForgotPasswordScreen />
              </AuthGuard>
            }
          />

          {/* OTP routes - require state data from previous pages */}
          <Route
            path="/verify-otp-method"
            element={
              <AuthGuard
                requireStateData={true}
                allowedFromPaths={["/verify-otp"]}
              >
                <LazyVerifyOTPMethodScreen />
              </AuthGuard>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <AuthGuard
                requireStateData={true}
                allowedFromPaths={[
                  "/register",
                  "/forgot-password",
                  "/update-profile",
                  "/verify-otp-method",
                  "/verify-otp",
                ]}
              >
                <LazyVerifyOTPScreen />
              </AuthGuard>
            }
          />
          <Route
            path="/forgot-password-confirm"
            element={
              <AuthGuard
                requireNoAuth={true}
                requireStateData={true}
                allowedFromPaths={["/verify-otp"]}
              >
                <LazyResetPasswordScreen />
              </AuthGuard>
            }
          />
          <Route
            path="/profile-setup"
            element={
              <ProfileSetupRoute>
                <ProfileGuard allowIncompleteProfile={true}>
                  <LazyProfileSetupScreen />
                </ProfileGuard>
              </ProfileSetupRoute>
            }
          />
          <Route
            path="/complete-profile"
            element={
              <ProfileSetupRoute>
                <ProfileGuard allowIncompleteProfile={true}>
                  <LazyProfileSetupScreen />
                </ProfileGuard>
              </ProfileSetupRoute>
            }
          />

          {/* Enhanced catch-all route with profile setup protection */}
          <Route
            path="*"
            element={
              <PrivateRoute>
                <div className="min-h-screen flex items-center justify-center bg-white">
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">
                      Chức năng đang phát triển
                    </h2>
                    <p className="text-gray-600 mb-4">
                      Vui lòng kiên nhẫn chờ Mebi cập nhật nhé
                    </p>
                    <button
                      onClick={() => globalThis.history.back()}
                      className="px-4 py-2 bg-mebi-yellow-500 text-white rounded-lg hover:bg-mebi-yellow-600"
                    >
                      Quay lại
                    </button>
                  </div>
                </div>
              </PrivateRoute>
            }
          />
          <Route
            path="/redirect-role"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <LazyRedirectRoleScreen />
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/player-home"
            element={
              <AuthGuard requireAuth={true}>
                <Navigate to="/tabs/home" />
              </AuthGuard>
            }
          />
          <Route
            path="/tabs"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <LazyMainTabs />
                </ProfileGuard>
              </AuthGuard>
            }
          >
            <Route path="newsfeed" element={<LazyAppHomeScreen />} />
            <Route path="schedule" element={<LazyMyScheduleScreen />} />
            <Route path="home" element={<LazyPlayerHomeScreen />} />
            <Route path="statistics" element={<LazyPlayerStatisticsScreen />} />
            <Route
              path="tournaments"
              element={<LazyRegisterTournamentScreen />}
            />
            <Route
              path="tournament-history"
              element={<LazyTournamentHistoryScreen />}
            />
            <Route
              path="tournaments/detail/:id"
              element={<LazyTournamentDetailScreen />}
            />
            {/* Add other tab routes here as needed */}
            <Route index element={<PlayerTabsIndexRedirect />} />
            <Route
              path="ranking"
              element={<Navigate to="/tabs/ranking/leaderboard" />}
            />
            <Route
              path="ranking/leaderboard"
              element={<LazyLeaderBoardScreen />}
            />
            <Route
              path="ranking/information"
              element={<LazyRankingInformationScreen />}
            />
            <Route
              path="ranking/players/:playerId"
              element={<LazyPlayerRankingInformationScreen />}
            />
            <Route
              path="ranking/players/:playerId/settlement/:settlementGuid"
              element={<LazyRankingSettlementDetail />}
            />
          </Route>

          {/* Organizer Tabs - Protected by OrganizerGuard */}
          <Route
            path="/organizer-tabs"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyOrganizerTabs />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          >
            <Route path="newsfeed" element={<LazyAppHomeScreen />} />
            <Route
              path="tournaments"
              element={<LazyRegisterTournamentScreen />}
            />
            <Route path="my-tournaments" element={<LazyMyTournamentScreen />} />
            <Route
              path="tournaments/detail/:id"
              element={<LazyTournamentDetailScreen />}
            />
            <Route
              path="statistics"
              element={<LazyOrganizationStatisticsScreen />}
            />
            <Route path="home" element={<LazyOrganizerHomeScreen />} />
            <Route index element={<OrganizerTabsIndexRedirect />} />
          </Route>

          {/* Standalone screens accessible via navigation (not tabs) */}
          <Route
            path="/create-tournament"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyCreateTournamentScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/registration-progress"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <LazyRegistrationProgressScreen />
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/registration-progress/:registrationId"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <LazyRegistrationProgressScreen />
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/registration-list/:competitionId?"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyRegistrationListScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <LazyNotificationScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <LazySettingScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/account"
            element={
              <PrivateRoute>
                <LazyAccountScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/update-profile"
            element={
              <PrivateRoute>
                <LazyUpdateProfileScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <PrivateRoute>
                <LazyChangePasswordScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <PrivateRoute>
                <LazySubscriptionScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <LazyReportScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/organizer-registration"
            element={
              <PrivateRoute>
                <LazyOrganizerRegistrationScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/match-history"
            element={
              <PrivateRoute>
                <LazyMatchHistoryScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-schedule"
            element={
              <PrivateRoute>
                <LazyMyScheduleScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/player-profile/:id?"
            element={
              <PrivateRoute>
                <LazyPlayerProfileScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/player-achievements/:playerId?"
            element={
              <PrivateRoute>
                <LazyPlayerAchievementsScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/organizer-profile"
            element={
              <PrivateRoute>
                <LazyOrganizerProfileScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/organizer-profile/:slug"
            element={
              <PrivateRoute>
                <LazyOrganizerHomeScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/tournament-detail/:id"
            element={<LazyTournamentDetailScreen />}
          />
          <Route
            path="/competition/:id"
            element={
              <PrivateRoute>
                <LazyCompetitionDetailScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/competition/:id/list"
            element={
              <PrivateRoute>
                <LazyCompetitionListScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/competition/:categoryId/schedule"
            element={
              <PrivateRoute>
                <LazyCompetitionScheduleScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/competition/:categoryId/results"
            element={
              <PrivateRoute>
                <LazyCompetitionResultsScreen />
              </PrivateRoute>
            }
          />
          <Route
            path="/competition/:categoryId/add-prize"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyAddPrizeScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/competition/:categoryId/manage-results"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyCategoryManagementScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/edit-bracket/:categoryId"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyEditBracketScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/draw-position/:categoryId"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyDrawPositionScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
          <Route
            path="/random/:categoryId"
            element={
              <AuthGuard requireAuth={true}>
                <ProfileGuard>
                  <OrganizerGuard>
                    <LazyDrawPositionScreen />
                  </OrganizerGuard>
                </ProfileGuard>
              </AuthGuard>
            }
          />
        </Routes>
      </RouteGuard>
    </>
  );
};

export default AppRouter;
